import React from 'react';
import Head from 'next/head';
import { Global, css } from '@emotion/react';
import { fontAsset } from '@/features/common/fontAssets';

/**
 * Paperlogy 웹폰트 선언.
 *
 * 라이선스: SIL Open Font License 1.1 (웹 임베딩 허용).
 * 원본 Paperlogy 1.000 TTF 9종 중 디자인에서 실제로 쓰는 굵기만 woff2로 변환해 쓴다.
 * 완성형 한글 전체 + 라틴 + 문장부호를 포함한 서브셋이라 문구가 바뀌어도 깨지지 않는다.
 *
 * 공용 resources/ltp/fonts/common/paperlogy/에서 받는다.
 * 폰트를 못 받아오면 Pretendard로 대체 렌더된다 (font-display: swap).
 *
 * 메인·특장점·과목 페이지가 같은 폰트 파일과 CDN 캐시를 공유한다.
 */

/**
 * 디자인이 쓰는 굵기. @font-face 선언과 preload가 같은 목록을 본다.
 *
 * 2026-09-29 디자인 요청으로 제목 굵기가 모두 800이 되면서 600·700은 쓰는 곳이 없어
 * 뺐다(S3의 600·700 파일은 남아 있다). 다시 쓰게 되면 파일명과 굵기를 함께 추가한다.
 *
 * CDN에서 장기 캐시로 나가야 하므로(S3 오브젝트 메타데이터의 Cache-Control)
 * 파일을 교체할 때는 반드시 파일명을 바꾸고 이 목록도 함께 갱신한다.
 */
export const PAPERLOGY_FONT_FILES = ['paperlogy-800.woff2'] as const;

export const paperlogyFontUrl = (file: string): string => fontAsset('paperlogy', file);

export default function PaperlogyFontFace() {
    return (
        <>
            <Head>
                {/*
                 * @font-face만 있으면 브라우저는 이 서체를 쓰는 텍스트를 레이아웃한 뒤에야 파일을 요청해
                 * swap 전까지 Pretendard로 그려진 헤드라인이 잠깐 보인다(FOUT).
                 * preload로 HTML 파싱 시점에 받기 시작해 그 구간을 줄인다.
                 * 폰트 preload는 same-origin이어도 crossorigin="anonymous"가 없으면 재사용되지 않는다.
                 */}
                {PAPERLOGY_FONT_FILES.map((file) => (
                    <link
                        key={file}
                        rel="preload"
                        as="font"
                        type="font/woff2"
                        href={paperlogyFontUrl(file)}
                        crossOrigin="anonymous"
                    />
                ))}
            </Head>
            <Global
                styles={css`
                    @font-face {
                        font-family: 'Paperlogy';
                        font-style: normal;
                        font-weight: 800;
                        font-display: swap;
                        src: url('${paperlogyFontUrl('paperlogy-800.woff2')}') format('woff2');
                    }
                `}
            />
        </>
    );
}
